import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Hammer, Lock, Loader2 } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { useSEO } from "@/hooks/useSEO";

export default function AdminLogin() {
  useSEO({ title: "Admin Login", description: "WAAN METAL admin portal.", noindex: true });
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user && user.role === "admin") navigate("/admin"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A1128] px-4 relative overflow-hidden">
      <div className="absolute inset-0 hero-grid-overlay opacity-30" />
      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <span className="flex items-center justify-center w-11 h-11 rounded-md bg-gradient-to-br from-[#B87333] to-[#D97706] text-[#0A1128]"><Hammer size={24} strokeWidth={2.5} /></span>
          <span className="font-display font-extrabold text-white text-2xl">WAAN<span className="text-[#D97706]">METAL</span></span>
        </div>
        <form onSubmit={submit} className="bg-white rounded-2xl p-8 shadow-2xl" data-testid="admin-login-form">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Lock size={22} className="text-[#B87333]" /> Admin Login</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">Sign in to manage enquiries and content.</p>
          {error && <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="admin-login-error">{error}</div>}
          <div className="space-y-4">
            <div><label className="text-sm font-medium text-slate-700 block mb-1.5">Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-login-email" className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] outline-none" /></div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1.5">Password</label><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-login-password" className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] outline-none" /></div>
          </div>
          <button type="submit" disabled={busy} data-testid="admin-login-submit" className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gradient-to-r from-[#B87333] to-[#D97706] text-[#0A1128] font-bold hover:-translate-y-0.5 transition-all disabled:opacity-60">
            {busy ? <Loader2 size={18} className="animate-spin" /> : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
